import * as React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalFooter } from "@/components/modal"
import { ConfirmDialog } from "@/components/confirm-dialog"
import { api } from "@/lib/api"
import { useNotificationStore } from "@/store"
import { z } from "zod"
import {
  Bot,
  Plus,
  Trash2,
  RefreshCw,
  Wifi,
  WifiOff,
  TestTube,
  Webhook,
  XCircle,
  Settings,
  CheckCircle,
} from "lucide-react"

const botSchema = z.object({
  name: z.string().min(1, "Name is required"),
  token: z.string().min(1, "Token is required"),
})

export default function BotsPage() {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)
  const [isFormOpen, setIsFormOpen] = React.useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false)
  const [selectedBot, setSelectedBot] = React.useState(null)
  const [formValues, setFormValues] = React.useState({ name: "", token: "" })
  const [formErrors, setFormErrors] = React.useState({})

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["bots"],
    queryFn: () => api.get("/bots"),
  })

  const createMutation = useMutation({
    mutationFn: (data) => api.post("/bots", data),
    onSuccess: () => {
      queryClient.invalidateQueries(["bots"])
      addNotification({ type: "success", message: "Bot created successfully" })
      setIsFormOpen(false)
      setFormValues({ name: "", token: "" })
    },
    onError: (error) => addNotification({ type: "error", message: error.message }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/bots/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["bots"])
      addNotification({ type: "success", message: "Bot deleted successfully" })
      setIsDeleteOpen(false)
      setSelectedBot(null)
    },
    onError: (error) => addNotification({ type: "error", message: error.message }),
  })

  const testMutation = useMutation({
    mutationFn: (id) => api.post(`/bots/${id}/test`),
    onSuccess: (res) => {
      addNotification({ type: "success", message: res.message || "Connection successful" })
      queryClient.invalidateQueries(["bots"])
    },
    onError: (error) => addNotification({ type: "error", message: error.message }),
  })

  const webhookMutation = useMutation({
    mutationFn: ({ id, action, url }) =>
      action === "set" ? api.post(`/bots/${id}/webhook`, { url }) : api.delete(`/bots/${id}/webhook`),
    onSuccess: (res) => {
      addNotification({ type: "success", message: res.message || "Webhook updated" })
      queryClient.invalidateQueries(["bots"])
    },
    onError: (error) => addNotification({ type: "error", message: error.message }),
  })

  const handleSubmit = () => {
    const result = botSchema.safeParse(formValues)
    if (!result.success) {
      const errors = {}
      result.error.issues.forEach((i) => { errors[i.path[0]] = i.message })
      setFormErrors(errors)
      return
    }
    setFormErrors({})
    createMutation.mutate(formValues)
  }

  const bots = data?.data || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bots</h1>
          <p className="text-muted-foreground">Telegram bot management</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => { setFormValues({ name: "", token: "" }); setIsFormOpen(true) }}>
            <Plus className="mr-2 h-4 w-4" />
            Add Bot
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {bots.map((bot) => (
          <Card key={bot.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Bot className="h-4 w-4" />
                  {bot.name}
                </CardTitle>
                <Badge variant={bot.is_connected ? "default" : "secondary"}>
                  {bot.is_connected ? (
                    <><CheckCircle className="mr-1 h-3 w-3" /> Connected</>
                  ) : (
                    <><XCircle className="mr-1 h-3 w-3" /> Disconnected</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">Chat ID:</span> {bot.chat_id || "N/A"}
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">Username:</span> @{bot.username || "N/A"}
              </div>
              {bot.webhook_url && (
                <div className="text-sm text-muted-foreground truncate">
                  <span className="font-medium">Webhook:</span> {bot.webhook_url}
                </div>
              )}
              <div className="flex items-center gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => testMutation.mutate(bot.id)}
                  disabled={testMutation.isPending}
                >
                  <TestTube className="mr-1 h-3 w-3" />
                  Test
                </Button>
                {bot.webhook_url ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => webhookMutation.mutate({ id: bot.id, action: "delete" })}
                    disabled={webhookMutation.isPending}
                  >
                    <XCircle className="mr-1 h-3 w-3" />
                    Remove Webhook
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const url = prompt("Enter webhook URL:")
                      if (url) webhookMutation.mutate({ id: bot.id, action: "set", url })
                    }}
                    disabled={webhookMutation.isPending}
                  >
                    <Webhook className="mr-1 h-3 w-3" />
                    Set Webhook
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setSelectedBot(bot); setIsDeleteOpen(true) }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {bots.length === 0 && !isLoading && (
          <Card className="col-span-full">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Bot className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No bots configured yet</p>
            </CardContent>
          </Card>
        )}
      </div>

      <Modal open={isFormOpen} onOpenChange={setIsFormOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Add Bot</ModalTitle>
          </ModalHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={formValues.name}
                onChange={(e) => setFormValues((v) => ({ ...v, name: e.target.value }))}
                placeholder="Bot name"
                className={formErrors.name ? "border-destructive" : ""}
              />
              {formErrors.name && <p className="text-xs text-destructive mt-1">{formErrors.name}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Token</label>
              <Input
                value={formValues.token}
                onChange={(e) => setFormValues((v) => ({ ...v, token: e.target.value }))}
                placeholder="Telegram bot token"
                type="password"
                className={formErrors.token ? "border-destructive" : ""}
              />
              {formErrors.token && <p className="text-xs text-destructive mt-1">{formErrors.token}</p>}
            </div>
          </div>
          <ModalFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Creating..." : "Create Bot"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmDialog
        open={isDeleteOpen}
        onClose={() => { setIsDeleteOpen(false); setSelectedBot(null) }}
        onConfirm={() => selectedBot && deleteMutation.mutate(selectedBot.id)}
        title="Delete Bot"
        description={`Are you sure you want to delete "${selectedBot?.name}"? This action cannot be undone.`}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
